import { useState, useEffect, useCallback, useRef } from 'react';
import {
  doc,
  getDoc,
  onSnapshot,
  updateDoc as firestoreUpdateDoc,
  deleteDoc as firestoreDeleteDoc,
  DocumentData,
  Unsubscribe,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';

interface UseFirestoreDocOptions {
  collection: string;
  id: string;
  realtime?: boolean;
}

interface UseFirestoreDocResult<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  updateDoc: (data: Partial<T>) => Promise<void>;
  deleteDoc: () => Promise<void>;
}

export function useFirestoreDoc<T extends DocumentData>(
  options: UseFirestoreDocOptions
): UseFirestoreDocResult<T> {
  const { collection, id, realtime = false } = options;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const unsubscribeRef = useRef<Unsubscribe | null>(null);

  const docRef = doc(db, collection, id);

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);

    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    if (realtime) {
      unsubscribeRef.current = onSnapshot(
        docRef,
        (snapshot) => {
          if (snapshot.exists()) {
            setData({ id: snapshot.id, ...snapshot.data() } as T);
          } else {
            setData(null);
          }
          setLoading(false);
          setError(null);
        },
        (err) => {
          setError(err);
          setLoading(false);
        }
      );
    } else {
      getDoc(docRef)
        .then((snapshot) => {
          if (snapshot.exists()) {
            setData({ id: snapshot.id, ...snapshot.data() } as T);
          } else {
            setData(null);
          }
          setLoading(false);
        })
        .catch((err) => {
          setError(err);
          setLoading(false);
        });
    }
  }, [collection, id, realtime]);

  useEffect(() => {
    fetchData();

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [fetchData]);

  const updateDocument = useCallback(
    async (updates: Partial<T>) => {
      await firestoreUpdateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    },
    [collection, id]
  );

  const deleteDocument = useCallback(async () => {
    await firestoreDeleteDoc(docRef);
    setData(null);
  }, [collection, id]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
    updateDoc: updateDocument,
    deleteDoc: deleteDocument,
  };
}
